import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Avis } from '../../../models/avis';
import { ConditionsCreationMapService } from '../../shared/services/conditions-creation-map.service';

/**
 * Interface représentant un champ extrait de la définition du formulaire.
 */
interface ExtractedField {
  id: string;
  jsonPath: string;
  conditionsCreationMap?: { [key: string]: string[] };
  pattern?: string;
  parentGroupId?: string;
  listname?: string;
}

/**
 * Service responsable de l'enrichissement du JSON d'un avis avec les inputIds.
 *
 * Ce service reproduit côté frontend la logique d'enrichissement utilisée lors de la création standard
 * (voir EformFormulaireService.setInputId et FormInputFactory.setInputId).
 *
 * L'enrichissement consiste à ajouter les propriétés 'inputId' à chaque champ du JSON
 * en se basant sur le mapping jsonPath -> inputId défini dans les formulaires.
 *
 * Note: Les services existants (EformFormulaireService, FormInputFactory) travaillent sur des FormGroup Angular.
 * Pour l'import, nous devons travailler directement sur le JSON brut, d'où ce service dédié.
 */
@Injectable({
  providedIn: 'root'
})
export class InputIdEnrichmentService {

  // Cache pour les définitions de formulaires
  private formDefinitionsCache: Map<string, any> = new Map();

  constructor(
    private httpClient: HttpClient,
    private conditionsCreationMapService: ConditionsCreationMapService
  ) { }

  /**
   * Enrichit le JSON de l'avis avec les inputIds.
   * Parcourt les champs de la définition du formulaire et pour chaque champ,
   * navigue dans le JSON pour ajouter l'inputId correspondant.
   *
   * @param avis L'avis à enrichir
   * @returns Observable<Avis> L'avis enrichi avec les inputIds
   */
  public enrichWithInputIds(avis: Avis): Observable<Avis> {
    if (!avis || !avis.formulaire?.code) {
      return of(avis);
    }

    const formCode = avis.formulaire.code;

    return this.getFormFields(formCode).pipe(
      map(fields => {
        if (fields.length > 0 && avis.jsonSaisieFormulaire?.ann) {

          let enrichedTotal = 0;
          fields.forEach(field => {
            if (field.id && field.jsonPath) {
              const count = this.enrichFieldInJson(
                avis.jsonSaisieFormulaire.ann,
                field.jsonPath,
                field.id,
                field
              );
              if (count > 0) {
                enrichedTotal += count;
              }
            }
          });

        }
        return avis;
      }),
      catchError(error => {
        console.error('❌ [InputIdEnrichment] Erreur:', error);
        return of(avis);
      })
    );
  }

  /**
   * Récupère la liste des champs pour un formulaire donné.
   *
   * @param formCode Le code du formulaire (ex: E_FORM_25)
   * @returns Observable<Array<ExtractedField>> La liste des champs
   */
  private getFormFields(formCode: string): Observable<ExtractedField[]> {
    if (this.formDefinitionsCache.has(formCode)) {
      return of(this.extractFields(this.formDefinitionsCache.get(formCode)));
    }

    const formDefinitionPath = `assets/dynamic/creer/formSections/${formCode}.json`;

    return this.httpClient.get<any>(formDefinitionPath).pipe(
      map(formDefinition => {
        this.formDefinitionsCache.set(formCode, formDefinition);
        return this.extractFields(formDefinition);
      }),
      catchError(error => {
        console.warn(`[InputIdEnrichment] Formulaire ${formCode} non trouvé:`, error);
        return of([]);
      })
    );
  }

  /**
   * Extrait récursivement tous les champs avec leur id, jsonPath et conditionsCreationMap.
   * Propage les conditionsCreationMap des groupes parents vers les champs enfants.
   */
  private extractFields(
    obj: any,
    fields: ExtractedField[] = [],
    parentConditionsCreationMap?: { [key: string]: string[] },
    parentGroupId?: string
  ): ExtractedField[] {
    if (!obj || typeof obj !== 'object') {
      return fields;
    }

    // Récupérer la conditionsCreationMap de l'objet courant (groupe ou champ)
    const currentConditionsCreationMap = obj.conditionsCreationMap || parentConditionsCreationMap;
    const currentGroupId = obj.id && obj.contentType === 'group' ? obj.id : parentGroupId;

    if (obj.id && obj.jsonPath && obj.contentType === 'field') {
      fields.push({
        id: obj.id,
        jsonPath: obj.jsonPath,
        conditionsCreationMap: currentConditionsCreationMap,
        pattern: obj.pattern,
        parentGroupId: currentGroupId,
        listname: obj.listname
      });
    }

    if (Array.isArray(obj)) {
      obj.forEach(item => this.extractFields(item, fields, parentConditionsCreationMap, parentGroupId));
    } else {
      // Pour les groupes, propager la conditionsCreationMap aux enfants
      const conditionsToPropagate = obj.conditionsCreationMap || parentConditionsCreationMap;
      const groupIdToPropagate = obj.contentType === 'group' && obj.id ? obj.id : parentGroupId;

      Object.values(obj).forEach(value => {
        if (value && typeof value === 'object') {
          this.extractFields(value, fields, conditionsToPropagate, groupIdToPropagate);
        }
      });
    }

    return fields;
  }

  /**
   * Enrichit un champ dans le JSON en naviguant via le jsonPath.
   * Gère les tableaux avec [] et la correspondance case-insensitive.
   * Prend en compte les conditionsCreationMap pour différencier les champs avec le même jsonPath.
   *
   * @param json Le JSON à enrichir
   * @param jsonPath Le chemin du champ (ex: "contractawardnotice.issuetime.val")
   * @param inputId L'inputId à ajouter
   * @param fieldConfig Configuration du champ incluant les conditionsCreationMap et listname
   * @returns Le nombre de champs enrichis
   */
  private enrichFieldInJson(
    json: any,
    jsonPath: string,
    inputId: string,
    fieldConfig?: ExtractedField
  ): number {
    if (!json || !jsonPath) {
      return 0;
    }

    // Parser le jsonPath
    let pathParts = jsonPath.split('.');

    // Les attributs finaux qui ne sont pas des objets mais des propriétés directes de l'objet parent
    const LEAF_ATTRIBUTES = ['val', 'listname', 'listName', 'schemename', 'languageid', 'id'];

    // Supprimer l'attribut final (val, listName, etc.) car on veut enrichir l'objet parent
    const lastPart = pathParts[pathParts.length - 1];
    if (LEAF_ATTRIBUTES.some(attr => attr.toLowerCase() === lastPart?.toLowerCase())) {
      pathParts.pop();
    }

    // Gérer les cas où conditionsCreationMap mentionne un objet intermédiaire
    // qui n'est pas dans le jsonPath mais qui existe dans le JSON réel
    if (fieldConfig?.conditionsCreationMap) {
      pathParts = this.insertMissingIntermediateObjects(pathParts, fieldConfig.conditionsCreationMap);
    }

    // Naviguer dans le JSON en utilisant le chemin
    const enrichedCount = this.navigateAndEnrich(json, pathParts, 0, inputId, fieldConfig, new Map(), fieldConfig?.listname);

    return enrichedCount;
  }

  /**
   * Insère les objets intermédiaires manquants dans le path en se basant sur conditionsCreationMap.
   *
   * Exemple 1:
   * - pathParts: ["contractawardnotice", "procurementprojectlot[]", "tenderingprocess", "contractingsystem[]"]
   * - conditionsCreationMap: {"contractingsystem": ["[ContractingSystemTypeCode/&listName='framework-agreement']"]}
   * - Résultat: [..., "contractingsystem[]", "contractingsystemtypecode"]
   *
   * Exemple 2 (chemin à plusieurs niveaux):
   * - pathParts: ["contractawardnotice", "procurementprojectlot[]", "tenderingterms", "awardcriterionparameter[]"]
   * - conditionsCreationMap: {"awardcriterionparameter": ["[ParameterCode/FieldIdentifierCode/&text()='awa-cri-wei']"]}
   * - Résultat: [..., "awardcriterionparameter[]", "parametercode", "fieldidentifiercode"]
   *
   * IMPORTANT: Extrait TOUS les segments du chemin dans les conditions (séparés par /),
   * en ignorant les attributs simples de la blacklist.
   *
   * @param pathParts Les parties du path actuel
   * @param conditionsCreationMap Map des conditions de création
   * @returns Un nouveau tableau de path avec les objets intermédiaires insérés
   */
  private insertMissingIntermediateObjects(pathParts: string[], conditionsCreationMap: any): string[] {
    const newPathParts = [...pathParts];

    // Liste noire des noms qui sont des attributs/conditions, pas des objets intermédiaires
    const BLACKLIST = [
      'id',                    // Attribut simple
      'listname',             // Attribut simple
      'schemename',           // Attribut simple
      'languageid',           // Attribut simple
      'format',               // Attribut simple
      'unitcode'              // Attribut simple
    ];

    for (const condKey of Object.keys(conditionsCreationMap)) {
      const conditions = conditionsCreationMap[condKey];
      if (!conditions || !Array.isArray(conditions)) continue;

      for (const condition of conditions) {
        // Extraire le chemin complet avant &text() ou &listName
        // Pattern: [ParameterCode/FieldIdentifierCode/&text()='awa-cri-wei']
        //          [ContractingSystemTypeCode/&listName='framework-agreement']
        const pathMatch = condition.match(/\[([A-Za-z/]+)\/&(?:text|listName)/);
        if (!pathMatch || !pathMatch[1]) {
          continue;
        }

        // Diviser le chemin en segments (ex: "ParameterCode/FieldIdentifierCode" -> ["ParameterCode", "FieldIdentifierCode"])
        const pathSegments = pathMatch[1].split('/').filter(s => s.length > 0);

        // Trouver le point d'insertion dans le path (après le condKey)
        const condKeyWithArray = condKey + '[]';
        const insertIndex = newPathParts.findIndex(part =>
          part.toLowerCase() === condKey.toLowerCase() ||
          part.toLowerCase() === condKeyWithArray.toLowerCase()
        );

        if (insertIndex === -1) {
          continue;
        }

        let currentInsertPos = insertIndex + 1;

        // Insérer chaque segment du path qui n'est pas déjà présent
        for (const segment of pathSegments) {
          const segmentLower = segment.toLowerCase();

          // Ignorer les attributs simples de la blacklist
          if (BLACKLIST.includes(segmentLower)) {
            continue;
          }

          // Vérifier si ce segment est déjà présent après la position courante
          const alreadyPresent = newPathParts.slice(currentInsertPos).some(part =>
            part.toLowerCase().replace('[]', '') === segmentLower
          );

          if (!alreadyPresent) {
            // Insérer le segment à la position courante
            newPathParts.splice(currentInsertPos, 0, segmentLower);
          }

          // Avancer la position d'insertion (que le segment ait été inséré ou qu'il était déjà là)
          currentInsertPos++;
        }
      }
    }

    return newPathParts;
  }

  /**
   * Navigue récursivement dans le JSON et enrichit les objets trouvés.
   * Gère les tableaux (notation []) et la correspondance case-insensitive.
   * Vérifie les conditionsCreationMap pour les tableaux afin d'appliquer le bon inputId.
   *
   * @param obj L'objet courant dans lequel naviguer
   * @param pathParts Les parties du chemin à parcourir
   * @param index L'index courant dans pathParts
   * @param inputId L'inputId à ajouter aux objets cibles
   * @param fieldConfig Configuration du champ incluant conditionsCreationMap et listname
   * @param arrayContexts Map des contextes de tableaux rencontrés (nom du tableau -> élément)
   *                      Permet de gérer les tableaux imbriqués et de vérifier les conditions sur les parents
   * @param parentListname Listname hérité du parent (pour propager aux enfants)
   */
  private navigateAndEnrich(
    obj: any,
    pathParts: string[],
    index: number,
    inputId: string,
    fieldConfig?: ExtractedField,
    arrayContexts: Map<string, any> = new Map(),
    parentListname?: string
  ): number {
    if (!obj || index >= pathParts.length) {
      // On est arrivé à la fin du chemin - obj est maintenant l'objet à enrichir
      // Accepter tout objet JSON (avec 'val', 'listName', ou autre attribut)
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        // Vérifier les conditionsCreationMap si présentes
        if (fieldConfig?.conditionsCreationMap) {
          if (!this.conditionsCreationMapService.matchesConditionsWithContexts(fieldConfig.conditionsCreationMap, arrayContexts)) {
            return 0;
          }
        }

        let enriched = false;

        const existingInputId = obj.inputId || obj.inputid;

        // On enrichit si :
        // - pas encore d'inputId, ou
        // - l'inputId existant ne correspond pas à celui attendu (correction d'une valeur erronée)
        const alreadyCorrect = existingInputId &&
          existingInputId.toLowerCase() === inputId.toLowerCase();

        if (!alreadyCorrect) {
          obj.inputId = inputId;
          enriched = true;
        }

        // NOTE: On ne modifie PAS obj.listname car dans le JSON, la propriété listname/listName
        // contient la VALEUR sélectionnée (ex: 'competition' pour BT-03-notice), pas le nom
        // du référentiel de codes. Modifier cette propriété écraserait la valeur du champ.

        return enriched ? 1 : 0;
      }
      return 0;
    }

    let currentPart = pathParts[index];
    const isArray = currentPart.includes('[]');
    const keyName = currentPart.replace('[]', '');

    // Trouver la clé correspondante (case-insensitive)
    const actualKey = this.findKeyIgnoreCase(obj, keyName);
    if (!actualKey) {
      return 0;
    }

    const value = obj[actualKey];
    if (!value) {
      return 0;
    }

    let enrichedCount = 0;
    const effectiveListname = fieldConfig?.listname || parentListname;

    if (isArray && Array.isArray(value)) {
      value.forEach((item) => {
        const newContexts = new Map(arrayContexts);
        newContexts.set(keyName.toLowerCase(), item);
        enrichedCount += this.navigateAndEnrich(item, pathParts, index + 1, inputId, fieldConfig, newContexts, effectiveListname);
      });
    } else if (isArray && typeof value === 'object') {
      const newContexts = new Map(arrayContexts);
      newContexts.set(keyName.toLowerCase(), value);
      enrichedCount += this.navigateAndEnrich(value, pathParts, index + 1, inputId, fieldConfig, newContexts, effectiveListname);
    } else if (typeof value === 'object') {
      enrichedCount += this.navigateAndEnrich(value, pathParts, index + 1, inputId, fieldConfig, arrayContexts, effectiveListname);
    }

    return enrichedCount;
  }

  /**
   * Trouve une clé dans un objet en ignorant la casse.
   */
  private findKeyIgnoreCase(obj: any, keyName: string): string | null {
    if (!obj || typeof obj !== 'object') {
      return null;
    }

    const lowerKey = keyName.toLowerCase();
    for (const key of Object.keys(obj)) {
      if (key.toLowerCase() === lowerKey) {
        return key;
      }
    }
    return null;
  }


  /**
   * Vide le cache des définitions de formulaires.
   */
  public clearCache(): void {
    this.formDefinitionsCache.clear();
  }
}
